import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  acquireSubscription,
  type SubscriptionHandle,
} from "@/features/chat/api/realtime";
import { useAuthStore } from "@/store/auth";
import type { UserBrief } from "../types";

export interface IncomingMeetingInvite {
  meetingId: string;
  occurrenceId: string;
  title: string;
  from: UserBrief | null;
}

interface PersonalEvent {
  type: "meeting_invite" | "meeting_started" | "meeting_cancelled";
  meeting_id: string;
  occurrence_id?: string;
  title?: string;
  from?: UserBrief | null;
}

/**
 * Personal `meetings:{user_id}` channel — instant-meeting rings and lifecycle
 * pings. Mounted once (MeetingInvitesHost in the protected layout); regular
 * scheduled-meeting notifications go through the bell instead.
 */
export function useMeetingInvites() {
  const userId = useAuthStore((s) => s.user?.id) ?? null;
  const qc = useQueryClient();
  const [incoming, setIncoming] = useState<IncomingMeetingInvite | null>(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    let handle: SubscriptionHandle | null = null;
    let handler: ((ctx: { data: unknown }) => void) | null = null;

    (async () => {
      try {
        const h = await acquireSubscription(`meetings:${userId}`);
        if (cancelled) {
          h.release();
          return;
        }
        handle = h;
        handler = (ctx) => {
          const event = ctx.data as PersonalEvent;
          if (event.type === "meeting_invite" && event.occurrence_id) {
            setIncoming({
              meetingId: event.meeting_id,
              occurrenceId: event.occurrence_id,
              title: event.title ?? "Spotkanie",
              from: event.from ?? null,
            });
          }
          // Any lifecycle ping — refresh meeting queries.
          void qc.invalidateQueries({ queryKey: ["meetings"] });
        };
        h.sub.on("publication", handler);
      } catch {
        /* Centrifugo unavailable — invites simply won't ring. */
      }
    })();

    return () => {
      cancelled = true;
      try {
        if (handler) handle?.sub.off("publication", handler);
        handle?.release();
      } catch {
        /* ignore */
      }
    };
  }, [userId, qc]);

  return { incoming, dismiss: () => setIncoming(null) };
}
